<template>
  <div
    class="manageable-select flex flex-col gap-1.5 min-w-0 w-full"
    :class="{ 'manageable-select--editing': isCreateMode || isEditMode }"
  >
    <InputGroup class="manageable-select__group w-full min-w-0">
      <FloatLabel variant="on" class="flex-1 min-w-0 w-full">
        <Select
          v-if="!isCreateMode && !isEditMode"
          :inputId="inputId"
          v-model="model"
          :options="options"
          :optionLabel="optionLabel"
          :optionValue="optionValue"
          class="w-full min-w-0 max-w-full"
          inputClass="w-full min-w-0 max-w-full truncate rounded-md"
          :disabled="disabled"
          :invalid="invalid"
          :filter="filter"
          :showClear="showClear"
          :loading="loading"
        />
        <InputText
          v-else
          :id="inputId"
          ref="nameInputRef"
          v-model="draftName"
          class="w-full min-w-0 max-w-full truncate rounded-md"
          :maxlength="maxLength"
          autocomplete="off"
          :disabled="disabled || isSaving"
          :invalid="!!displayError"
          :placeholder="resolvedPlaceholder"
          :aria-label="resolvedPlaceholder"
          @keydown.enter.prevent="confirmAction"
          @keydown.escape.prevent="cancelAction"
        />
        <label :for="inputId" class="text-sm font-medium">
          {{ label }}
          <span v-if="required" class="text-red-500">*</span>
        </label>
      </FloatLabel>

      <template v-if="showCatalogActions && !isCreateMode && !isEditMode">
        <InputGroupAddon v-if="canAdd" class="manageable-select__addon">
          <Button
            icon="pi pi-plus"
            type="button"
            severity="secondary"
            variant="text"
            class="manageable-select__action-btn"
            :aria-label="addAriaLabel"
            :disabled="disabled || addDisabled || isSaving"
            v-tooltip.top="resolvedAddTooltip"
            @click="openCreateMode"
          />
        </InputGroupAddon>
        <InputGroupAddon v-if="canEdit && hasSelection" class="manageable-select__addon">
          <Button
            icon="pi pi-pencil"
            type="button"
            severity="info"
            variant="text"
            class="manageable-select__action-btn manageable-select__action-btn--edit"
            aria-label="Edit selected option"
            :disabled="disabled || editDisabled || isSaving"
            v-tooltip.top="editTooltip"
            @click="openEditMode"
          />
        </InputGroupAddon>
        <InputGroupAddon v-if="canDelete && hasSelection" class="manageable-select__addon">
          <Button
            icon="pi pi-trash"
            type="button"
            severity="danger"
            variant="text"
            class="manageable-select__action-btn manageable-select__action-btn--delete"
            aria-label="Delete selected option"
            :disabled="disabled || deleteDisabled || isSaving"
            v-tooltip.top="deleteTooltip"
            @click="confirmDelete"
          />
        </InputGroupAddon>
      </template>

      <template v-if="isCreateMode || isEditMode">
        <InputGroupAddon class="manageable-select__addon">
          <Button
            icon="pi pi-check"
            type="button"
            severity="success"
            variant="text"
            class="manageable-select__action-btn"
            :loading="isSaving"
            :disabled="disabled || isSaving || !draftName.trim()"
            :aria-label="isCreateMode ? confirmAriaLabel : 'Save changes'"
            v-tooltip.top="isCreateMode ? confirmAriaLabel : 'Save changes'"
            @click="confirmAction"
          />
        </InputGroupAddon>
        <InputGroupAddon class="manageable-select__addon">
          <Button
            icon="pi pi-times"
            type="button"
            severity="secondary"
            variant="text"
            class="manageable-select__action-btn"
            :disabled="disabled || isSaving"
            aria-label="Cancel"
            v-tooltip.top="'Cancel'"
            @click="cancelAction"
          />
        </InputGroupAddon>
      </template>
    </InputGroup>

    <small v-if="(isCreateMode || isEditMode) && contextHint" class="text-xs text-surface-500 dark:text-surface-400 pl-1">
      {{ contextHint }}
    </small>
    <small v-if="displayError" class="text-xs text-red-500 pl-1">
      {{ displayError }}
    </small>
    <small v-if="hint && !isCreateMode && !isEditMode" class="text-xs text-surface-500 dark:text-surface-400 pl-1">
      {{ hint }}
    </small>
    <small v-if="invalid && invalidMessage && !isCreateMode && !isEditMode" class="text-xs text-red-500 pl-1">
      {{ invalidMessage }}
    </small>

    <p class="sr-only" aria-live="polite">{{ liveMessage }}</p>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import FloatLabel from 'primevue/floatlabel';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] },
  optionLabel: { type: String, default: 'label' },
  optionValue: { type: String, default: 'value' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  invalidMessage: { type: String, default: '' },
  filter: { type: Boolean, default: true },
  showClear: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  canAdd: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
  addDisabled: { type: Boolean, default: false },
  editDisabled: { type: Boolean, default: false },
  deleteDisabled: { type: Boolean, default: false },
  addDisabledMessage: { type: String, default: '' },
  editDisabledMessage: { type: String, default: '' },
  deleteDisabledMessage: { type: String, default: '' },
  addLabel: { type: String, default: '' },
  createPlaceholder: { type: String, default: 'New name' },
  editPlaceholder: { type: String, default: 'Edit name' },
  contextHint: { type: String, default: '' },
  hint: { type: String, default: '' },
  maxLength: { type: Number, default: 190 },
  inputId: { type: String, default: '' },
  /** async (name) => option */
  createOption: { type: Function, default: null },
  /** async (currentValue, name) => option */
  updateOption: { type: Function, default: null },
  /** async (value) => void */
  deleteOption: { type: Function, default: null },
  deleteConfirmHeader: { type: String, default: '' },
  deleteConfirmMessage: { type: String, default: '' },
});

const model = defineModel({ default: null });
const confirm = useConfirm();

const isCreateMode = ref(false);
const isEditMode = ref(false);
const draftName = ref('');
const editingValue = ref(null);
const isSaving = ref(false);
const localError = ref('');
const liveMessage = ref('');
const nameInputRef = ref(null);

const addAriaLabel = computed(() => props.addLabel || `Create ${props.label}`);
const confirmAriaLabel = computed(() => props.addLabel || `Add ${props.label}`);
const displayError = computed(() => localError.value);
const hasSelection = computed(() => {
  const value = model.value;
  return value != null && String(value).trim() !== '';
});

const resolvedPlaceholder = computed(() =>
  isEditMode.value ? props.editPlaceholder : props.createPlaceholder
);

const resolvedAddTooltip = computed(() => {
  if (props.addDisabled && props.addDisabledMessage) return props.addDisabledMessage;
  return addAriaLabel.value;
});

const editTooltip = computed(() => props.editDisabledMessage || 'Edit selected option');
const deleteTooltip = computed(() => props.deleteDisabledMessage || 'Delete selected option');
const showCatalogActions = computed(
  () =>
    props.canAdd ||
    (props.canEdit && hasSelection.value) ||
    (props.canDelete && hasSelection.value)
);

function focusNameInput() {
  nextTick(() => {
    const inst = nameInputRef.value;
    const root = inst?.$el ?? inst;
    const input = root?.querySelector?.('input') ?? root;
    if (input?.focus) input.focus();
  });
}

function openCreateMode() {
  if (props.disabled || props.addDisabled || !props.canAdd || isSaving.value) return;
  isCreateMode.value = true;
  isEditMode.value = false;
  draftName.value = '';
  editingValue.value = null;
  localError.value = '';
  focusNameInput();
}

function openEditMode() {
  if (props.disabled || props.editDisabled || !props.canEdit || !hasSelection.value || isSaving.value) {
    return;
  }
  isEditMode.value = true;
  isCreateMode.value = false;
  editingValue.value = model.value;
  draftName.value = String(model.value ?? '');
  localError.value = '';
  focusNameInput();
}

function cancelAction() {
  if (isSaving.value) return;
  isCreateMode.value = false;
  isEditMode.value = false;
  draftName.value = '';
  editingValue.value = null;
  localError.value = '';
}

async function confirmAction() {
  const name = draftName.value.trim();
  if (!name) {
    localError.value = 'Name is required';
    return;
  }

  isSaving.value = true;
  localError.value = '';
  try {
    if (isCreateMode.value) {
      if (typeof props.createOption !== 'function') return;
      const option = await props.createOption(name);
      const value = option?.value ?? option?.[props.optionValue] ?? name;
      model.value = value;
      liveMessage.value = `${props.label} "${option?.label ?? name}" selected.`;
    } else if (isEditMode.value) {
      if (typeof props.updateOption !== 'function') return;
      const option = await props.updateOption(editingValue.value, name);
      const value = option?.value ?? option?.[props.optionValue] ?? name;
      model.value = value;
      liveMessage.value = `${props.label} updated to "${option?.label ?? name}".`;
    }
    isCreateMode.value = false;
    isEditMode.value = false;
    draftName.value = '';
    editingValue.value = null;
  } catch (err) {
    localError.value = err?.message || err?.error?.message || 'Action failed';
  } finally {
    isSaving.value = false;
  }
}

function confirmDelete() {
  if (
    props.disabled ||
    props.deleteDisabled ||
    !props.canDelete ||
    !hasSelection.value ||
    typeof props.deleteOption !== 'function'
  ) {
    return;
  }

  const selected = model.value;
  const selectedOption = (props.options || []).find(
    (row) => String(row?.[props.optionValue] ?? row?.value) === String(selected)
  );
  const selectedLabel = selectedOption?.[props.optionLabel] || selected;
  confirm.require({
    message:
      props.deleteConfirmMessage ||
      `Delete "${selectedLabel}" from the list? Existing employee records will keep this value until edited.`,
    header: props.deleteConfirmHeader || `Delete ${props.label}`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    rejectProps: { severity: 'secondary', outlined: true },
    acceptLabel: 'Delete',
    acceptProps: { severity: 'danger' },
    accept: async () => {
      isSaving.value = true;
      localError.value = '';
      try {
        await props.deleteOption(selected);
        model.value = null;
        liveMessage.value = `${props.label} "${selected}" deleted.`;
      } catch (err) {
        localError.value = err?.message || err?.error?.message || 'Failed to delete';
      } finally {
        isSaving.value = false;
      }
    },
  });
}

watch(
  () => [props.canAdd, props.canEdit, props.canDelete, props.addDisabled, props.disabled],
  () => {
    if (props.disabled) cancelAction();
  }
);
</script>

<style scoped>
.manageable-select__action-btn {
  min-width: 2.25rem;
  width: 2.25rem;
}

.manageable-select--editing :deep(.p-floatlabel label) {
  color: var(--p-primary-color);
}

.manageable-select__group {
  max-width: 100%;
  min-width: 0;
  align-items: stretch;
}

.manageable-select__addon {
  flex-shrink: 0;
  padding: 0;
  display: flex;
  align-self: stretch;
}

.manageable-select__addon :deep(.p-button) {
  border-radius: 0;
  height: 100%;
  align-self: stretch;
}

.manageable-select__group :deep(.p-floatlabel) {
  flex: 1 1 0%;
  min-width: 0;
}

.manageable-select__group :deep(.p-select),
.manageable-select__group :deep(.p-inputtext) {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
}

.manageable-select__group :deep(.p-select .p-select-label) {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.manageable-select__group > :deep(.p-floatlabel:first-child) .p-select,
.manageable-select__group > :deep(.p-floatlabel:first-child) .p-inputtext {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.manageable-select__group > :deep(.p-inputgroupaddon:last-child) {
  border-top-right-radius: var(--p-form-field-border-radius, 6px);
  border-bottom-right-radius: var(--p-form-field-border-radius, 6px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
